import { BlockStack, Card, DataTable, EmptyState, Text } from '@shopify/polaris';
import Pagination from 'components/pagination';
import SimpleFilter from 'components/simpleFilter';
import helpers from 'helpers/index';
import { ___ } from 'languages/index';
import dateandtime from 'date-and-time';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import emptyIMG from '../../media/images/empty.png';
import { ENUM_INVOICE_STATUS, TypedInvoices, useGetInvoices } from 'queries/invoices.query';

interface Props {
  vehicle_id: string;
}

export default function IncomeLog({ vehicle_id }: Props) {
  const history = useNavigate();
  const { search } = useLocation();
  const StringQuery: any = helpers.ExtractUrl(search);

  const initialQuery = useMemo(
    () => ({
      query: '',
      page: 1,
      limit: 25,
      sort: 'createdAt:desc',
      'trip_manifest_invoice.vehicle_id': vehicle_id,
    }),
    [vehicle_id]
  );

  const [mainQuery, setMainQuery] = useState<any>({
    ...initialQuery,
    ...StringQuery,
    vehicle_id,
  });

  const [entities, setEntities] = useState<TypedInvoices[]>([]);
  const [totalItems, setTotalItems] = useState(0);

  const { data, refetch: getEntities, isFetching: loading } = useGetInvoices(mainQuery);

  useEffect(() => {
    if (data) {
      setEntities(data.body?.records || []);
      setTotalItems(data.totalItems || 0);
    }
  }, [data]);

  useEffect(() => {
    setMainQuery((prev) => ({ ...prev, vehicle_id, page: 1 }));
  }, [vehicle_id]);

  useEffect(() => {
    const buildURLSearch = helpers.buildEndUrl(mainQuery);
    if (search !== buildURLSearch) {
      history(`/vehicle/${vehicle_id}/income` + buildURLSearch);
    }
    getEntities();
  }, [mainQuery]);

  const onChangePagination = useCallback((numPage: number, limit: number) => {
    setMainQuery((prev) => ({ ...prev, page: numPage, limit }));
  }, []);

  const filterCallback = useCallback(
    (value: any) => {
      if (value === false) {
        setMainQuery(initialQuery);
        return;
      }
      setMainQuery((prev) => ({ ...prev, ...value, page: 1, vehicle_id }));
    },
    [initialQuery, vehicle_id]
  );

  const rows = entities.map(({ trip_manifest_invoice, ...invoices }) => [
    trip_manifest_invoice.id || '-',
    invoices.shDon || '-',
    invoices.maTraCuu || '-',
    trip_manifest_invoice.trip_manifest?.trip?.journey?.journey_name || '-',
    invoices.TaxType || '-',
    helpers.formatNumberWithCommas(invoices.TgThTien || 0),
    helpers.formatNumberWithCommas(invoices.TgTThue || 0),
    helpers.formatNumberWithCommas(invoices.TgTTTBSo || 0),
    invoices.invoice_status === ENUM_INVOICE_STATUS.CANCEL ? 'Đã hủy' : invoices.is_signed === 1 ? 'Đã ký' : 'Nháp',
    invoices.createdAt ? dateandtime.format(new Date(Number(invoices.createdAt)), 'HH:mm:ss DD-MM-YYYY') : '-',
  ]);

  const emptyData = (
    <EmptyState heading="Không có thu nhập!" image={emptyIMG}>
      <p>Không có dữ liệu phù hợp. Hãy thử bỏ bộ lọc hoặc tìm kiếm từ khóa khác.</p>
    </EmptyState>
  );

  return (
    <BlockStack gap="400">
      <Card padding="0">
        <SimpleFilter loading={loading} onCallback={filterCallback} options={[]} sortField={[]} defaultSort="createdAt:desc" />

        {entities?.length > 0 ? (
          <DataTable
            hoverable
            fixedFirstColumns={1}
            columnContentTypes={['text', 'text', 'text', 'text', 'text', 'numeric', 'numeric', 'numeric', 'text', 'text']}
            headings={['ID', 'Số hóa đơn', 'Mã tra cứu', 'Tuyến', 'Loại thuế', 'Tiền trước thuế', 'Tiền thuế', 'Tổng tiền', 'Trạng thái', 'Tạo']}
            rows={rows}
            hideScrollIndicator
            footerContent={___('Show page {display_page_number} of {total_record_number} results', {
              display_page_number: <Text as="strong">{mainQuery?.page || 1}</Text>,
              total_record_number: <Text as="strong">{totalItems}</Text>,
            })}
          />
        ) : (
          emptyData
        )}
      </Card>

      {totalItems > mainQuery.limit ? (
        <Pagination TotalRecord={totalItems} activeCurrentPage={mainQuery.page} pageSize={mainQuery.limit} onChange={onChangePagination} />
      ) : null}
    </BlockStack>
  );
}

